// src/hooks/useBusinessGuard.js
import { useEffect, useState } from "react";
import { useAuth } from "../context/useAuth";
import { supabase } from "../utils/supabaseClient";

export default function useBusinessGuard() {
  const { user, profile, logout, loading, error } = useAuth();

  const [businessRow, setBusinessRow] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);

  // businesses.id is the integer primary key used by flyer_slots,
  // campaigns, coupons and other business-owned tables.
  const businessIdInt = profile?.business_id ?? null;
  const businessIdUuid = profile?.business_uuid ?? null;
  const authId = profile?.auth_id ?? user?.id ?? null;

  const campaignId = profile?.default_campaign_id ?? null;
  const groupId = profile?.default_group_id ?? null;
  const toolOrder = profile?.tool_order ?? [];

  useEffect(() => {
    let cancelled = false;

    const loadBusiness = async () => {
      if (loading) return;

      if (!authId) {
        if (!cancelled) {
          setBusinessRow(null);
          setBusinessLoading(false);
        }
        return;
      }

      setBusinessLoading(true);

      const { data, error: businessError } = await supabase
        .from("businesses")
        .select("*")
        .eq("auth_id", authId)
        .maybeSingle();

      if (cancelled) return;

      if (businessError) {
        console.error(
          "[BusinessGuard] Failed to load business row:",
          businessError.message
        );
        setBusinessRow(null);
      } else {
        setBusinessRow(data || null);
      }

      setBusinessLoading(false);
    };

    loadBusiness();

    return () => {
      cancelled = true;
    };
  }, [authId, loading]);

  const updateUserSettings = async (updates) => {
    if (!user?.id) {
      return { error: new Error("No user ID available") };
    }

    const { data, error: updateError } = await supabase
      .from("user_settings")
      .update(updates)
      .eq("user_id", user.id);

    if (updateError) {
      console.error(
        "[BusinessGuard] Failed to update user settings:",
        updateError.message
      );
    }

    return { data, error: updateError };
  };

  const combinedLoading = loading || businessLoading;

  return {
    user,
    profile,
    businessIdInt,
    businessIdUuid,
    authId,
    campaignId,
    groupId,
    toolOrder,
    businessRow,
    businessLoading,
    logout,
    updateUserSettings,
    loading: combinedLoading,
    error,
  };
}
